import React, { AnchorHTMLAttributes } from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import Link, { type LinkProps } from "next/link";
import { LinkArrowIcon } from "./LinkArrowIcon";

export enum ButtonVariantType {
  default = "default",
  small = "small",
  outline = "outline",
  outlineLight = "outlineLight",
  link = "link",
  linkArrow = "linkArrow",
  destructive = "destructive",
  secondary = "secondary",
  ghost = "ghost",
}

const DIRECTUS_VARIANT_MAP: Record<string, ButtonVariantType> = {
  default: ButtonVariantType.default,
  solid: ButtonVariantType.default,
  small: ButtonVariantType.small,
  outline: ButtonVariantType.outline,
  outline_light: ButtonVariantType.outlineLight,
  link: ButtonVariantType.link,
  link_arrow: ButtonVariantType.linkArrow,
  soft: ButtonVariantType.default,
  ghost: ButtonVariantType.link,
  secondary: ButtonVariantType.outline,
};

export const linkButtonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap font-sans ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none",
  {
    variants: {
      variant: {
        default:
          "rounded-none text-base font-normal uppercase tracking-normal leading-snug text-primary-foreground bg-primary hover:bg-primary-hover disabled:bg-disabled disabled:text-primary-foreground",
        small:
          "rounded-none text-xs font-normal uppercase tracking-normal leading-snug text-primary-foreground bg-primary hover:bg-primary-hover disabled:bg-disabled disabled:text-primary-foreground",
        outline:
          "rounded-none border border-primary bg-transparent text-base font-normal uppercase tracking-normal leading-snug text-primary hover:bg-primary hover:text-primary-foreground disabled:border-disabled disabled:text-disabled disabled:hover:bg-transparent",
        outlineLight:
          "rounded-none border border-secondary-foreground bg-transparent text-base font-normal uppercase tracking-normal leading-snug text-secondary-foreground hover:bg-secondary-foreground hover:text-primary disabled:border-disabled disabled:text-disabled disabled:hover:bg-transparent",
        link: "h-auto min-h-0 rounded-none p-0 text-link normal-case text-foreground underline decoration-solid underline-offset-4 hover:text-primary hover:decoration-primary disabled:text-disabled disabled:no-underline",
        linkArrow:
          "h-auto min-h-0 gap-4 rounded-none p-0 font-sans text-base font-normal leading-[1.2] uppercase tracking-normal text-secondary hover:text-primary disabled:text-disabled",
        destructive:
          "rounded-lg bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50",
        secondary:
          "rounded-lg bg-secondary text-secondary-foreground hover:bg-secondary/80 disabled:opacity-50",
        ghost:
          "rounded-lg hover:bg-accent hover:text-accent-foreground disabled:opacity-50",
      },
      size: {
        default: "min-h-[47px] px-6 py-3.5",
        sm: "min-h-8 px-4 py-2 text-xs font-normal uppercase",
        lg: "h-11 px-8",
        icon: "h-10 w-10",
        link: "h-auto min-h-0 p-0",
      },
    },
    compoundVariants: [
      { variant: "link", size: "default", class: "h-auto min-h-0 p-0" },
      { variant: "linkArrow", size: "default", class: "h-auto min-h-0 p-0" },
      { variant: "small", size: "default", class: "min-h-8 px-4 py-2 text-xs" },
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export const getButtonVariant = (variant: string): ButtonVariantType => {
  const mappedVariant = DIRECTUS_VARIANT_MAP[variant];
  if (mappedVariant) {
    return mappedVariant;
  }
  if (variant in ButtonVariantType) {
    return variant as ButtonVariantType;
  }
  return ButtonVariantType.default;
};

/** Шапка и прочие CTA «ссылка со стрелкой» — всегда linkArrow (uppercase + стрелка). */
export function toLinkArrowVariant(
  variant?: ButtonVariantType
): ButtonVariantType {
  if (
    variant === ButtonVariantType.link ||
    variant === ButtonVariantType.linkArrow
  ) {
    return ButtonVariantType.linkArrow;
  }
  return variant ?? ButtonVariantType.linkArrow;
}

type CombinedLinkProps = Omit<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  keyof LinkProps
> &
  LinkProps;

interface LinkButtonProps
  extends CombinedLinkProps,
    VariantProps<typeof linkButtonVariants> {}

export const LinkButton = React.forwardRef<HTMLAnchorElement, LinkButtonProps>(
  ({ className, variant, size, children, ...props }, ref) => {
    const resolvedVariant = variant ?? "default";
    const resolvedSize =
      resolvedVariant === "link" || resolvedVariant === "linkArrow"
        ? "link"
        : size;

    return (
      <Link
        className={cn(
          linkButtonVariants({ variant: resolvedVariant, size: resolvedSize, className })
        )}
        ref={ref}
        {...props}
      >
        {resolvedVariant === "linkArrow" ? (
          <>
            <span className="underline decoration-solid decoration-current underline-offset-4">
              {children}
            </span>
            <LinkArrowIcon />
          </>
        ) : (
          children
        )}
      </Link>
    );
  }
);

LinkButton.displayName = "LinkButton";
